import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Moon, Sun } from 'lucide-react';
import { getMode, setMode, isDarkModeAvailable, ThemeMode } from '../../theme/themeEngine';
import axios from '../../_services/axiositeachWrapper';
import PortletService, { Portlet, PortletIcon } from '../../_services/PortletService';
import { resolveIcon } from '../../constants/iconMap';
import IconDisplay from '../IconDisplay';
import Config from '../../_config/config';
import { WorkspaceConfig } from '../../pages/home/workspace/types';
import { buildDefaultWorkspaces } from '../../pages/home/workspace/workspaceDefaults';
import { languages } from '../../i18n/languages';
import { useAccessControl } from '../../access-control/AccessControlContext';
import './Sidebar.css';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  forceHide?: boolean; // Force hide sidebar completely (for fullscreen views)
}

// An emoji/text string OR a structured icon object (lucide/url/image) rendered via IconDisplay.
type MenuIcon = string | { type: string; value?: string; key?: string; color?: string };

// Lower-case Lucide icon name, e.g. "user-round-search", "settings-2", "link".
// Lowercase-only + no slash/dot so it never matches an emoji, URL/path, or UPPER_SNAKE key.
const asLucideName = (raw: string | undefined): string | null => {
  if (!raw) return null;
  const s = raw.trim();
  return /^[a-z][a-z0-9]*(-[a-z0-9]+)*$/.test(s) ? s : null;
};

/**
 * Normalises whatever icon shape a portlet carries into a MenuIcon. Handles the
 * cases the raw config produces: a proper lucide/url/image object, an object
 * whose `type` is wrong but whose `value` is a lucide name, and a bare string
 * (image path, or a lucide name saved without the wrapper object).
 */
const toMenuIcon = (icon: PortletIcon | string | null | undefined): MenuIcon => {
  if (!icon) return '';
  if (typeof icon === 'string') {
    if (/[/.]/.test(icon) || /^https?:/i.test(icon)) return { type: 'image', value: icon }; // URL / path
    const name = asLucideName(icon);
    return name ? { type: 'lucide', value: name } : icon; // lucide name, else emoji glyph text
  }
  if (icon.type === 'lucide' || icon.type === 'url' || icon.type === 'image') return icon;
  // Wrong type (e.g. "emoji"/"key") but the value is actually a lucide name.
  const name = icon.key ? null : asLucideName(icon.value);
  if (name) return { type: 'lucide', value: name, color: icon.color };
  return resolveIcon(icon); // emoji / key → glyph string
};

interface MenuItem {
  id: string;
  label: string;
  icon: MenuIcon;
  path: string;
  description?: string;
}

// Object icons (lucide/url/image) render as SVG/img; plain strings render as text emoji.
const renderMenuIcon = (icon: MenuIcon): React.ReactNode => {
  if (typeof icon === 'string') return icon;
  return icon ? <IconDisplay icon={icon} size={18} /> : null;
};

interface MenuGroup {
  title: string;
  items: MenuItem[];
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose, forceHide = false }) => {
  const history = useHistory();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { loaded: aclLoaded, canViewPage } = useAccessControl();
  const [isCollapsed, setIsCollapsed] = useState(() => {
    // Load collapse state from localStorage
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved === 'true';
  });
  const [menuGroups, setMenuGroups] = useState<MenuGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [langPopupOpen, setLangPopupOpen] = useState(false);
  // Dark mode toggle — availability is tenant-gated (known only after
  // app-properties.json loads), so re-check on every "themechange"
  const [darkAvailable, setDarkAvailable] = useState(isDarkModeAvailable());
  const [themeMode, setThemeMode] = useState<ThemeMode>(getMode());

  useEffect(() => {
    const handleThemeChange = () => {
      setDarkAvailable(isDarkModeAvailable());
      setThemeMode(getMode());
    };
    handleThemeChange();
    window.addEventListener('themechange', handleThemeChange);
    return () => window.removeEventListener('themechange', handleThemeChange);
  }, []);

  const toggleThemeMode = () => {
    setMode(getMode() === 'dark' ? 'light' : 'dark');
  };

  // On desktop, we don't want to use the isOpen prop - sidebar is always visible
  // On mobile, we use isOpen for overlay behavior
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', isCollapsed.toString());
    document.body.classList.toggle('sidebar-collapsed', isCollapsed && !isMobile);
    window.dispatchEvent(new Event('sidebar-toggled'));
  }, [isCollapsed, isMobile]);

  // Load portlets from database and build menu groups
  useEffect(() => {
    const loadMenuGroups = async () => {
      try {
        setLoading(true);
        const allPortlets = await PortletService.getPortlets();
        const portlets = PortletService.filterPortletsByRole(allPortlets);

        // Build a lookup map for quick portlet access by id
        const portletMap = new Map<string, Portlet>(portlets.map((p) => [p.id, p]));

        // Load the user's workspace config (same source as the dashboard)
        const currentUserStr = localStorage.getItem('currentUser');
        const userId = currentUserStr ? JSON.parse(currentUserStr)?.userId : null;

        let workspaceConfig: WorkspaceConfig | null = null;
        if (userId) {
          try {
            const resp = await axios.get<{ success: boolean; data: WorkspaceConfig | null }>(
              `${Config.nodeSiteUrl}v1/preferences?userId=${userId}&name=workspace-config`
            );
            if (resp.data?.success && resp.data.data) {
              workspaceConfig = resp.data.data;
            }
          } catch {
            // fall through to defaults
          }
        }

        // Fall back to building defaults from portlet categories (same as dashboard)
        if (!workspaceConfig) {
          workspaceConfig = buildDefaultWorkspaces(portlets);
        }

        // Build sidebar groups from workspace sections (skip "All Apps")
        const groups: MenuGroup[] = [];

        // Add Home item first under a Dashboards group
        const homeGroup: MenuGroup = {
          title: 'Dashboards',
          items: [{
            id: 'home',
            label: 'Home',
            icon: '🏠',
            path: '/home',
            description: 'Back to main dashboard'
          }]
        };

        const sortedWorkspaces = [...workspaceConfig.workspaces]
          .filter((ws) => !ws.isAllApps)
          .sort((a, b) => a.order - b.order);

        // Track which portlet IDs are assigned to any workspace section
        const assignedPortletIds = new Set<string>(
          sortedWorkspaces.flatMap((ws) => ws.portletIds)
        );

        for (const ws of sortedWorkspaces) {
          const items: MenuItem[] = ws.portletIds
            .map((id) => portletMap.get(id))
            .filter((p): p is Portlet => !!p)
            .map((portlet) => {
              const portletPath = portlet.route?.path || (portlet as any).url || '#';
              const iconValue = toMenuIcon(portlet.icon as PortletIcon | string);
              return {
                id: portlet.id,
                label: portlet.title,
                icon: iconValue,
                path: portletPath,
                description: portlet.description,
              };
            });

          if (ws.name === 'Dashboards') {
            homeGroup.items.push(...items);
          } else if (items.length > 0) {
            groups.push({ title: ws.name, items });
          }
        }

        // Collect portlets not assigned to any workspace section into "Other"
        const otherItems: MenuItem[] = portlets
          .filter((p) => !assignedPortletIds.has(p.id))
          .map((portlet) => {
            const portletPath = portlet.route?.path || (portlet as any).url || '#';
            const iconValue = toMenuIcon(portlet.icon as PortletIcon | string);
            return {
              id: portlet.id,
              label: portlet.title,
              icon: iconValue,
              path: portletPath,
              description: portlet.description,
            };
          });

        const finalGroups = [homeGroup, ...groups];
        if (otherItems.length > 0) {
          finalGroups.push({ title: 'Other', items: otherItems });
        }

        setMenuGroups(finalGroups);
      } catch (error) {
        console.error('Error loading menu groups:', error);
      } finally {
        setLoading(false);
      }
    };

    loadMenuGroups();
  }, []);

  /*
   * NOTE: defaultMenuGroups removed - fallback is now handled by
   * PortletService.getDefaultPortlets() to maintain single source of truth
   */

  /* Commented out - keeping for reference only
  const defaultMenuGroups: MenuGroup[] = [
    {
      title: 'Dashboards',
      items: [
        {
          id: 'home',
          label: 'Home',
          icon: '🏠',
          path: '/home',
          description: 'Back to main dashboard',
        },
        {
          id: 'director-dashboard',
          label: 'Director Dashboard - Courses',
          icon: '📚',
          path: '/director-dashboard',
          description: 'Course-based Performance View',
        },
        {
          id: 'director-dashboard-programs',
          label: 'Director Dashboard - Programs',
          icon: '🎓',
          path: '/director-dashboard-programs',
          description: 'Program-based Performance View',
        },
        {
          id: 'academic-dashboard',
          label: 'Academic Dashboard',
          icon: '📊',
          path: '/academic-dashboard',
          description: 'Student Performance & Tests',
        },
        {
          id: 'analytics',
          label: 'Analytics',
          icon: '📈',
          path: '/lms-analytics',
          description: 'LMS Analytics & Reports',
        },
        {
          id: 'dashboard',
          label: 'Dashboard',
          icon: '📋',
          path: '/home?activetab=dashboard',
          description: 'View insights & metrics',
        },
      ],
    },
    {
      title: 'Academic',
      items: [
        {
          id: 'lm-builder',
          label: 'Learning Modules',
          icon: '🎓',
          path: '/lm-builder',
          description: 'Create & manage courses',
        },
        {
          id: 'case-studies',
          label: 'Case Studies',
          icon: '📖',
          path: '/case-studies',
          description: 'Author cases for AI discussions',
        },
        {
          id: 'activities',
          label: 'Activities',
          icon: '📚',
          path: '/home?activetab=activities',
          description: 'Manage daily activities',
        },
        {
          id: 'attendance',
          label: 'Attendance',
          icon: '✓',
          path: '/attendance',
          description: 'Track attendance',
        },
        {
          id: 'timetable',
          label: 'Timetable',
          icon: '📅',
          path: '/timetable',
          description: 'View schedule',
        },
        {
          id: 'timetable-new',
          label: 'New Timetable',
          icon: '📆',
          path: '/timetable-new',
          description: 'Instructor schedule search',
        },
        {
          id: 'timecraft-input-management',
          label: 'TimeCraft Management',
          icon: '🗓️',
          path: '/timecraft-input-management',
          description: 'Generate & manage timetables, inputs & boards',
        },
        {
          id: 'schedules',
          label: 'Schedules & Timetables',
          icon: '📋',
          path: '/schedules',
          description: 'View timetables, instructor schedules & bird\'s eye view',
        },
        {
          id: 'classroom-shift-management',
          label: 'Rooms & Shifts',
          icon: '🏫',
          path: '/classroom-shift-management',
          description: 'Manage classrooms, shifts & schedules',
        },
        {
          id: 'teacher-schedule',
          label: 'Teacher Schedule & Substitution',
          icon: '👤',
          path: '/teacher-schedule',
          description: 'Find a teacher to assign substitutes when absent',
        },
        {
          id: 'substitution-requests',
          label: 'Substitution Requests',
          icon: '🔁',
          path: '/substitution-requests',
          description: 'Approve or reject teacher substitution requests',
        },
        {
          id: 'postbox-messages',
          label: 'Notification Outbox',
          icon: '📧',
          path: '/postbox-messages',
          description: 'View all queued/sent emails, SMS and WhatsApp messages',
        },
        {
          id: 'approve-modules',
          label: 'Approve Modules',
          icon: '✅',
          path: '/approve-modules',
          description: 'Review module requests',
        },
      ],
    },
    {
      title: 'Operations',
      items: [
        {
          id: 'sales',
          label: 'Sales',
          icon: '💼',
          path: '/home?activetab=sales',
          description: 'Follow-ups & Enquiries',
        },
        {
          id: 'student-affairs',
          label: 'Student Affairs',
          icon: '🏠',
          path: '/student-affairs',
          description: 'Hostel, Mess & Student Welfare',
        },
        {
          id: 'parent-management',
          label: 'Parent Management',
          icon: '👨‍👩‍👧',
          path: '/parent-management',
          description: 'Manage parent accounts & child linking',
        },
        {
          id: 'research-management',
          label: 'Masters & PhD',
          icon: '🔬',
          path: '/research-management',
          description: 'Research student lifecycle management',
        },
      ],
    },
    {
      title: 'AI Tools',
      items: [
        {
          id: 'doubts-dashboard',
          label: 'Doubts Clinic',
          icon: '❓',
          path: '/doubts-dashboard',
          description: 'Manage student doubts',
        },

        {
          id: 'rubric-evaluation',
          label: 'Rubric Evaluation',
          icon: '📝',
          path: '/rubric-evaluation',
          description: 'Automated bulk grading',
        },
        {
          id: 'ai-grading-studio',
          label: 'AI Grading Studio',
          icon: '✨',
          path: '/ai-grading-studio',
          description: 'AI-assisted manual grading',
        },
      ],
    },
  ];
  */

  const handleMenuItemClick = (path: string) => {
    history.push(path);
    // Only close on mobile
    if (isMobile) {
      onClose();
    }
  };

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname + location.search === path;
  };

  // On desktop, show sidebar based on collapse state, not isOpen
  // On mobile, show sidebar based on isOpen prop
  // If forceHide is true, don't show sidebar at all
  const shouldShowSidebar = forceHide ? false : (isMobile ? isOpen : true);
  const sidebarClassName = `sidebar ${shouldShowSidebar ? 'open' : ''} ${isCollapsed && !isMobile ? 'collapsed' : ''} ${forceHide ? 'force-hidden' : ''}`;

  // Don't render sidebar at all if forceHide is true
  if (forceHide) {
    return null;
  }

  return (
    <>
      {/* Overlay - only on mobile */}
      {isMobile && isOpen && <div className="sidebar-overlay" onClick={onClose} />}

      {/* Sidebar */}
      <div className={sidebarClassName}>
        <div className="sidebar-header">
          {!isCollapsed && <h2>{t('sidebar.navigation')}</h2>}
          {!isMobile && (
            <button
              className="collapse-button"
              onClick={toggleCollapse}
              aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {isCollapsed ? '›' : '‹'}
            </button>
          )}
          {isMobile && (
            <button className="close-button" onClick={onClose} aria-label="Close menu">
              ×
            </button>
          )}
        </div>

        <nav className="sidebar-nav" aria-label="Main navigation">
          {loading ? (
            <div className="menu-loading" style={{ padding: '20px', textAlign: 'center', color: 'var(--text-muted)' }}>
              {t('sidebar.loading')}
            </div>
          ) : (
            menuGroups.map((group) => (
              <div key={group.title} className="menu-group">
                {!isCollapsed && <div className="group-title">{group.title}</div>}
                {group.items.map((item) => {
                  const active = isActive(item.path);
                  return (
                    <button
                      key={item.id}
                      className={`menu-item ${active ? 'active' : ''}`}
                      onClick={() => handleMenuItemClick(item.path)}
                      title={isCollapsed ? item.label : item.description}
                      aria-label={item.label}
                    >
                      <span className="menu-icon" aria-hidden="true">{renderMenuIcon(item.icon)}</span>
                      {!isCollapsed && (
                        <div className="menu-content">
                          <span className="menu-label">{item.label}</span>
                          {item.description && (
                            <span className="menu-description">{item.description}</span>
                          )}
                        </div>
                      )}
                      {!isCollapsed && <span className="menu-arrow">›</span>}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </nav>

        {/* User Manuals — open in new tab so the manual gets a clean fullscreen view */}
        <div className="sidebar-help">
          {!isCollapsed && <div className="group-title">Help</div>}
          {[
            { slug: 'scheduling', label: 'Scheduling Manual', icon: '🗓️' },
            { slug: 'flip-class', label: 'FlipClass Manual', icon: '📘' },
          ].map((m) => (
            <button
              key={m.slug}
              className="menu-item"
              onClick={() => window.open(`/manuals/${m.slug}`, '_blank', 'noopener')}
              title={isCollapsed ? m.label : `${m.label} (opens in new tab)`}
              aria-label={m.label}
            >
              <span className="menu-icon" aria-hidden="true">{m.icon}</span>
              {!isCollapsed && (
                <div className="menu-content">
                  <span className="menu-label">{m.label}</span>
                </div>
              )}
              {!isCollapsed && <span className="menu-arrow" aria-hidden="true">↗</span>}
            </button>
          ))}
        </div>

        {/* System Admin — gated by access-control (mirrors the /tenant-preferences PageGuard).
            restrictedByDefault=true: Root-only until Root assigns roles. */}
        {aclLoaded && canViewPage('tenant-preferences', true) && (
          <div className="sidebar-help">
            {!isCollapsed && <div className="group-title">Admin</div>}
            <button
              className="menu-item"
              onClick={() => history.push('/tenant-preferences')}
              title="Tenant Preferences"
              aria-label="Tenant Preferences"
            >
              <span className="menu-icon" aria-hidden="true">⚙️</span>
              {!isCollapsed && (
                <div className="menu-content">
                  <span className="menu-label">Tenant Preferences</span>
                </div>
              )}
              {!isCollapsed && <span className="menu-arrow" aria-hidden="true">›</span>}
            </button>
          </div>
        )}

        {/* Dark mode toggle — only when the tenant has enabled dark mode */}
        {darkAvailable && (
          <div className="sidebar-theme-toggle">
            <button
              className="theme-toggle-btn"
              onClick={toggleThemeMode}
              aria-label={themeMode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              title={themeMode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              <span className="theme-toggle-icon" aria-hidden="true">
                {themeMode === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
              </span>
              {!isCollapsed && (
                <span className="theme-toggle-label">
                  {themeMode === 'dark' ? 'Light mode' : 'Dark mode'}
                </span>
              )}
            </button>
          </div>
        )}

        {/* Language Selector */}
        <div className="sidebar-lang-selector">
          {isCollapsed ? (
            <div className="lang-collapsed-wrapper">
              <button
                className="lang-globe-btn"
                onClick={() => setLangPopupOpen(!langPopupOpen)}
                title={t('sidebar.language')}
              >
                🌐
              </button>
              {langPopupOpen && (
                <div className="lang-popup">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      className={`lang-popup-btn ${i18n.language === lang.code ? 'active' : ''}`}
                      onClick={() => {
                        i18n.changeLanguage(lang.code);
                        setLangPopupOpen(false);
                      }}
                    >
                      {lang.nativeName}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="lang-buttons">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  className={`lang-btn ${i18n.language === lang.code ? 'active' : ''}`}
                  onClick={() => i18n.changeLanguage(lang.code)}
                  title={lang.nativeName}
                >
                  {lang.code.toUpperCase()}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="sidebar-footer">
          {!isCollapsed && (
            <>
              <p className="footer-text">{t('sidebar.footer_brand')} @ {Config.tenantDisplayName}</p>
              <p className="footer-version">{t('sidebar.footer_version')}</p>
            </>
          )}
          {isCollapsed && <span className="footer-icon" aria-hidden="true">📚</span>}
        </div>
      </div>
    </>
  );
};

export default Sidebar;
